import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { store } from 'react-notifications-component';
import { ImageType } from 'react-images-uploading';
import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import AddCircleIcon from '@material-ui/icons/AddCircle';

import { Slider } from '../../../../shared/components/widgets/slider/slider.component';
import { AddShowroomImageModal } from '../add-showroom-image-modal/add-showroom-image-modal.component';
import { AddImageModal } from '../add-image-modal/add-image-modal.component';
import SortableGalleryComp from '../../../../shared/components/widgets/sortable-Gallery/sortable-gallery.component';

export interface EditVisualsProps {
  data: Pick<UnitTypeUiModel, 'showRooms' | 'roomTypePhotos' | 'id'>;
  eventHandlers: {
    submitShowroomImageHandler: (roomName: string, image: ImageType) => void;
    submitImageHandler: (image: ImageType) => void;
    deleteImageHandler: (imageId: string) => void;
    deleteShowroomImageHandler: (imageId: string, roomId: string) => void;
    updateImageOrder: (images: { id: string; url: string; order: number }[]) => void;
  };
}
/**
 * functional component EditVisuals
 * @param {EditVisualsProps} holding question text
 */
export const EditVisuals: React.FC<EditVisualsProps> = ({ data, eventHandlers }) => {
  const [showAddShowroomImageModal, setShowAddShowroomModal] = useState<boolean>(false);
  const [showAddImageModal, setShowAddImageModal] = useState<boolean>(false);
  const { t } = useTranslation();

  useEffect(() => {
    return () => {
      store.removeNotification('visuals-info-failed');
      store.removeNotification('visuals-info-success');
    };
  }, []);

  const hasRoomTypePhotos = data?.roomTypePhotos && data.roomTypePhotos.length > 0;

  return (
    <div className="w-full rounded-lg bg-white shadow-md">
      <div className="flex p-4 flex-row justify-between items-center">
        <div className="text-2xl font-bold text-neutral-600">
          {t('UNIT_TYPE_MODULE.EDIT_VISUALS.TITLE')}
        </div>
        <button
          type="button"
          className={`transition-all duration-200 cursor-pointer ${
            hasRoomTypePhotos
              ? 'text-5xl text-primary-600  flex justify-end bg-transparent border-none'
              : 'border-2 border-primary-600 mx-auto rounded-lg py-6 px-6 text-xl flex items-center gap-2 text-primary-600 hover:bg-primary-50'
          }`}
          onClick={() => setShowAddImageModal(true)}
        >
          <AddCircleIcon fontSize="inherit" color="inherit" />
        </button>
      </div>
      <div className="flex-1 w-full mt-4 pb-8 rounded-lg bg-white p-6 flex flex-col">
        {data?.roomTypePhotos && (
          <div className="mb-8">
            <SortableGalleryComp
              data={{
                sliderItems: data.roomTypePhotos.map((item) => ({
                  imgSrc: item.url,
                  id: item.id
                }))
              }}
              eventHandlers={{
                deleteHandler: (imageId) => {
                  eventHandlers.deleteImageHandler(imageId);
                },
                sortHandler: (items) => {
                  eventHandlers.updateImageOrder(
                    items.map((item, index) => ({
                      id: item.id,
                      url: item.imgSrc,
                      order: index + 1
                    }))
                  );
                }
              }}
            />
          </div>
        )}

        <hr className="my-6 border-gray-200" />

        <div className="my-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-2xl font-bold text-neutral-600">
              {t('UNIT_TYPE_MODULE.EDIT_VISUALS.SHOWROOM_TITLE')}
            </h3>
            <button
              type="button"
              className="text-5xl text-primary-600  flex justify-end bg-transparent border-none"
              onClick={() => setShowAddShowroomModal(true)}
            >
              <AddCircleIcon fontSize="inherit" color="inherit" />
            </button>
          </div>

          {data?.showRooms && (
            <div className="space-y-4">
              {data.showRooms.map((showRoomObj) => (
                <div
                  key={showRoomObj.roomId}
                  className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-base font-semibold text-gray-800">
                      {showRoomObj.roomName}
                    </span>
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                      {showRoomObj.imagesList.length}{' '}
                      {showRoomObj.imagesList.length === 1 ? 'image' : 'images'}
                    </span>
                  </div>
                  <Slider
                    data={{
                      sliderItems: showRoomObj.imagesList.map((item) => ({
                        imgSrc: item.url,
                        id: item.id
                      }))
                    }}
                    eventHandlers={{
                      deleteHandler: (imageId) => {
                        eventHandlers.deleteShowroomImageHandler(imageId, showRoomObj.roomId);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <AddShowroomImageModal
        config={{ showModal: showAddShowroomImageModal }}
        eventHandlers={{
          cancelHandler: () => {
            setShowAddShowroomModal(false);
          },
          submitImageHandler: (roomName: string, image: ImageType) => {
            setShowAddShowroomModal(false);
            eventHandlers.submitShowroomImageHandler(roomName, image);
          }
        }}
      />
      <AddImageModal
        config={{ showModal: showAddImageModal }}
        eventHandlers={{
          cancelHandler: () => {
            setShowAddImageModal(false);
          },
          submitImageHandler: (image: ImageType) => {
            setShowAddImageModal(false);
            eventHandlers.submitImageHandler(image);
          }
        }}
      />
    </div>
  );
};

export default EditVisuals;
